import React, { useState, useEffect, useCallback, useRef } from 'react';
import { EventsService } from '../services/eventsService';
import { ResidentGoalsService } from '../services/residentGoalsService';
import { createGoIconApiService } from '../services/goiconApiService';
import { Activity, Recommendation, ResidentNeed, WellnessDimension, DimensionVector } from '../types/recommendations';
import { RecommendationMode } from '../services/programSettingsService';
import { scoreActivities, selectDiverse } from '../services/recommendationScorer';
import { filterEventsForGender } from '../utils/genderEventFilter';
import { Icon } from './Icon';
import './EventRecommendations.css';

interface EventRecommendationsProps {
  facilityId?: string;
  goiconResidentId?: string;
  apiKey?: string;
  isStaff?: boolean;
  isExpanded?: boolean;
  onToggle?: (nextExpanded: boolean) => void;
  residentNeed?: ResidentNeed;
  recommendationMode?: RecommendationMode;
  /** Supabase resident ID — required when recommendationMode === 'goals' */
  residentId?: string;
  /** Incremented by the goals section whenever the resident's goal set changes;
   *  triggers a goals re-fetch and re-score in goals mode. */
  goalsVersion?: number;
  /** CareDaily location ID — when present, goals are read from CareDaily instead of Supabase */
  careDailyLocationId?: string;
  /** Maps category UUID → WellnessDimension — required when recommendationMode === 'goals' */
  categoryIdToWellness?: Record<string, WellnessDimension>;
  goiconDimensionIdToWellness?: Record<number, WellnessDimension>;
  goiconDimensionIdToDrivenByLabel?: Record<number, string>;
  goiconDimensionIdToColor?: Record<number, string>;
  goiconDimensionIdToIcon?: Record<number, string>;
  /** Wellness bucket key -> category display meta; labels the Supports badge by
   *  the goal dimension that drove the pick, and names the per-card mix line. */
  wellnessDimensionMeta?: Record<string, { label: string; color?: string; icon?: string }>;
  /** Source Recommended Activities from GoIcon's personalized smart endpoint instead of facility activities. Default false. */
  useSmartRecommendations?: boolean;
}

/**
 * Build a ResidentNeed vector from the resident's active goals.
 * Each active goal contributes weight to its associated wellness dimension.
 * The result is normalized so all weights sum to 1.
 */
function buildNeedFromGoals(
  goals: Awaited<ReturnType<typeof ResidentGoalsService.getGoals>>,
  categoryIdToWellness: Record<string, WellnessDimension>
): ResidentNeed {
  const DIMS: WellnessDimension[] = ['mind', 'body', 'social', 'spirit', 'environment', 'purpose', 'financial', 'other'];
  const counts: Partial<Record<WellnessDimension, number>> = {};
  let total = 0;

  for (const goal of goals.filter((g) => !g.completed)) {
    if (!goal.categoryId) continue;
    const dim = categoryIdToWellness[goal.categoryId];
    if (!dim) continue;
    counts[dim] = (counts[dim] ?? 0) + 1;
    total++;
  }

  const need: DimensionVector = {};
  for (const d of DIMS) {
    need[d] = total > 0 ? (counts[d] ?? 0) / total : 0;
  }
  return { need };
}

export const EventRecommendations: React.FC<EventRecommendationsProps> = ({
  facilityId,
  goiconResidentId,
  apiKey,
  isStaff = false,
  isExpanded: isExpandedProp,
  onToggle,
  residentNeed,
  recommendationMode = 'deficit',
  residentId,
  careDailyLocationId,
  categoryIdToWellness,
  goiconDimensionIdToWellness,
  goiconDimensionIdToDrivenByLabel,
  goiconDimensionIdToColor,
  goiconDimensionIdToIcon,
  useSmartRecommendations,
  goalsVersion,
  wellnessDimensionMeta
}) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const isExpanded = typeof isExpandedProp === 'boolean' ? isExpandedProp : internalIsExpanded;
  const PAGE_SIZE = 3;
  const [rawEvents, setRawEvents] = useState<Array<Activity | Recommendation>>([]);
  const [events, setEvents] = useState<Array<Activity | Recommendation>>([]);
  const [, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [goalsNeed, setGoalsNeed] = useState<ResidentNeed | null>(null);
  // True only when the interests-driven smart endpoint actually served the
  // current list (flag on, resident known, no fallback) — gates the
  // "based on your interests" claim in the subtitle.
  const [smartSourceActive, setSmartSourceActive] = useState(false);
  // Smart endpoint answered with zero activities (no interests set, or none
  // relevant) — the list came from the activities API and a notice explains why.
  const [noInterests, setNoInterests] = useState(false);
  // Need dimensions (goal areas / lowest wellness areas) with ZERO matching
  // events in the current pool — drives the "nothing scheduled for X" caption.
  const [unmetNeedDims, setUnmetNeedDims] = useState<string[]>([]);
  const lastFetchKeyRef = useRef<string | null>(null);

  const setExpanded = (nextExpanded: boolean) => {
    onToggle?.(nextExpanded);
    if (typeof isExpandedProp !== 'boolean') {
      setInternalIsExpanded(nextExpanded);
    }
  };

  // When mode is 'goals', load the resident's active goals and build the need vector
  useEffect(() => {
    if (recommendationMode !== 'goals' || !residentId || !facilityId || !categoryIdToWellness) {
      setGoalsNeed(null);
      return;
    }
    ResidentGoalsService.getGoals(residentId, facilityId, careDailyLocationId)
      .then((goals) => {
        const need = buildNeedFromGoals(goals, categoryIdToWellness);
        const hasAnyGoal = Object.values(need.need).some((v) => (v ?? 0) > 0);
        setGoalsNeed(hasAnyGoal ? need : null);
        console.log('🎯 Goals-driven need vector:', need, hasAnyGoal ? '(active)' : '(no active goals — falling back to deficit)');
      })
      .catch((err) => {
        console.warn('⚠️ Could not load goals for recommendation scoring:', err);
        setGoalsNeed(null);
      });
    // goalsVersion: bumped by GoalsTracker on add/complete/delete so the need
    // vector (and therefore the scored list) follows goal changes without a reload.
  }, [recommendationMode, residentId, facilityId, careDailyLocationId, categoryIdToWellness, goalsVersion]);

  const fetchEventRecommendations = useCallback(async () => {
    if (!facilityId || !apiKey) {
      console.log('⚠️ Missing required data for event recommendations');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const eventsService = new EventsService({
        facilityId,
        goiconResidentId: goiconResidentId || undefined,
        apiKey,
        goiconDimensionIdToWellness,
        useSmartRecommendations
      });

      // Fire the gender lookup alongside the activities fetch — don't serialize them.
      // No goiconResidentId → skip the lookup entirely and keep gender null (unfiltered).
      const genderPromise = goiconResidentId
        ? createGoIconApiService(apiKey).getResidentGender(facilityId, goiconResidentId)
        : Promise.resolve(null);

      const [{ activities, isFallback, smartNoMatches }, gender] = await Promise.all([
        eventsService.getEventRecommendationsWithFallback(),
        genderPromise,
      ]);
      console.log('📦 Event recommendations result:', activities.length, 'items');
      setSmartSourceActive(useSmartRecommendations === true && !!goiconResidentId && !isFallback && !smartNoMatches);
      setNoInterests(smartNoMatches === true);

      const filtered = filterEventsForGender(activities, gender);
      console.log('🚻 Gender filter:', gender ?? 'unknown', '-', activities.length, '->', filtered.length, 'events');
      setRawEvents(filtered);
    } catch (err) {
      console.error('❌ Failed to load event recommendations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  }, [facilityId, goiconResidentId, apiKey, goiconDimensionIdToWellness, useSmartRecommendations]);

  useEffect(() => {
    console.log('🔎 EventRecommendations useEffect:', {
      facilityId: facilityId || '(empty)',
      goiconResidentId: goiconResidentId || '(empty)',
      apiKey: apiKey ? '✅ present' : '❌ missing',
      isStaff,
      isExpanded,
      recommendationMode,
    });
    if (!facilityId || !apiKey) {
      console.log('🚫 EventRec: skipping — missing facilityId or apiKey');
      return;
    }
    // goiconResidentId is used for group filtering only. Fetch activities regardless —
    // if it's absent the groups call returns an empty Set and all activities are shown.
    if (!goiconResidentId) {
      console.log('⚠️ EventRec: goiconResidentId not resolved — group filtering will be skipped');
    }
    // Fetch eagerly regardless of expand state so the list is ready when the section opens.
    // Includes useSmartRecommendations: programSettings loads async after mount (see Dashboard.tsx),
    // so the flag commonly flips false -> true post-mount and must trigger its own refetch.
    const fetchKey = `${facilityId}::${goiconResidentId}::${apiKey}::${useSmartRecommendations ? 'smart' : 'activities'}`;
    if (fetchKey === lastFetchKeyRef.current) {
      console.log('🚫 EventRec: skipping — already fetched for this key');
      return;
    }
    lastFetchKeyRef.current = fetchKey;
    setRawEvents([]);
    setEvents([]);
    setVisibleCount(PAGE_SIZE);
    setError(null);
    console.log('🚀 EventRec: firing fetch!');
    fetchEventRecommendations();
  }, [facilityId, goiconResidentId, apiKey, isStaff, isExpanded, recommendationMode, useSmartRecommendations, fetchEventRecommendations]);

  // Whether we're in goals mode but have nothing to score against
  const goalsModeMissingGoals = recommendationMode === 'goals' && !goalsNeed;

  // Re-score whenever raw events, need vector, or mode changes
  useEffect(() => {
    if (rawEvents.length === 0 || goalsModeMissingGoals) {
      setEvents([]);
      return;
    }

    const MAX_RECOMMENDATIONS = 5;

    const activeNeed = recommendationMode === 'goals' && goalsNeed ? goalsNeed : residentNeed;

    // Score by need, then spread across the resident's need dimensions so a
    // strong dimension (e.g. body) doesn't monopolize every slot and hide the
    // resident's mind/spirit goals. Without a need vector, keep source order.
    let top: Array<Activity | Recommendation>;
    if (activeNeed) {
      const scored = scoreActivities(rawEvents, activeNeed, { noveltyBoost: 0 });
      // Only events that actually touch the resident's need dimensions are
      // recommendations — never silently pad with unrelated events. If fewer
      // than MAX match, show fewer. Only when NOTHING matches do we fall back
      // to the general list, with the note explaining why.
      const matching = scored.filter((r) => (r.score ?? 0) > 0);
      const needDims = Object.entries(activeNeed.need)
        .filter(([, v]) => (v ?? 0) > 0)
        .map(([k]) => k);
      if (matching.length > 0 || needDims.length === 0) {
        const pool = matching.length > 0 ? matching : scored;
        top = selectDiverse(pool, activeNeed.need, MAX_RECOMMENDATIONS);
        setUnmetNeedDims([]);
      } else {
        top = selectDiverse(scored, activeNeed.need, MAX_RECOMMENDATIONS);
        setUnmetNeedDims(needDims);
      }
    } else {
      top = rawEvents.slice(0, MAX_RECOMMENDATIONS);
      setUnmetNeedDims([]);
    }

    top.sort((a, b) => {
      const aMs = a.startTime ? new Date(a.startTime).getTime() : Infinity;
      const bMs = b.startTime ? new Date(b.startTime).getTime() : Infinity;
      return aMs - bMs;
    });

    setEvents(top);
  }, [rawEvents, residentNeed, goalsNeed, recommendationMode, goalsModeMissingGoals]);

  // Say only what's true for this facility's configuration: goals mode scores
  // against the resident's goals; deficit mode balances the wellness mix; the
  // interests clause appears only when the smart endpoint served the list.
  const subtitle = recommendationMode === 'goals'
    ? `Personalized activity recommendations${smartSourceActive ? ' based on your interests' : ''} to help you achieve your goals`
    : `Personalized activity recommendations${smartSourceActive ? ' based on your interests' : ''} to help balance your wellness mix`;

  return (
    <div className="event-recommendations">
      <div className="event-recommendations-header">
        <div 
          className="event-recommendations-title-row"
          onClick={() => setExpanded(!isExpanded)}
        >
          <div>
            <h3>Event Recommendations</h3>
            <p className="event-recommendations-subtitle">{subtitle}</p>
            {/* Only explain the fallback when it actually produced events; with no
                goals or no events, the empty-state line below says it on its own. */}
            {noInterests && !loading && events.length > 0 && (
              <p className="event-recommendations-notice">
                No interests set, or no activities relevant to your interests. Showing activities
                based on your{recommendationMode === 'goals' ? ' goals' : ' wellness mix'}. Set
                interests on your profile to personalize further.
              </p>
            )}
          </div>
          <button
            type="button"
            className={`toggle-button ${isExpanded ? 'expanded' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!isExpanded);
            }}
            aria-label={isExpanded ? 'Collapse event recommendations' : 'Expand event recommendations'}
          >
            {isExpanded ? (
              <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            ) : (
              <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            )}
          </button>
        </div>
      </div>

      <div
        className={`collapsible-content ${isExpanded ? 'expanded' : ''}`}
        aria-hidden={!isExpanded}
      >
        <div className="collapsible-inner">
          <div className="event-recommendations-content">
            {loading && (
              <div className="event-recommendations-skeleton">
                <div className="skeleton skeleton-card"></div>
                <div className="skeleton skeleton-card"></div>
                <div className="skeleton skeleton-card"></div>
              </div>
            )}
          
            {!loading && error && (
              <div className="error-message">
                <p>Unable to load event recommendations: {error}</p>
              </div>
            )}
          
            {!loading && !error && events.length === 0 && (
              <p>
                {goalsModeMissingGoals
                  ? 'No goals added. Add goals above to see goal-driven recommendations.'
                  : 'No event recommendations available at this time.'}
              </p>
            )}
          
            {!loading && !error && events.length > 0 && (() => {
              return (
                <>
                  {unmetNeedDims.length > 0 && (
                    <p className="event-recommendations-unmet-note">
                      {(() => {
                        const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                        const names = unmetNeedDims
                          .map((k) => wellnessDimensionMeta?.[k]?.label ?? capitalize(k))
                          .join(' or ');
                        return `No ${names} activities are on the upcoming schedule — showing other activities you might enjoy.`;
                      })()}
                    </p>
                  )}
                  <div className="events-list">
                    {events.map((event) => (
                      <div key={event.id} className="event-item">
                        <div className="event-item-header">
                          <h4>{(event.title || '').trim()}</h4>
                          {facilityId && (event as any).goiconStartedDate && (
                            <a
                              className="event-register-button"
                              href={`https://api.goicon.com/facilities/${encodeURIComponent(String(facilityId))}/social/events/calendar/wellness-event/${encodeURIComponent(String(event.id))}/occurrence/${encodeURIComponent(String((event as any).goiconStartedDate))}`}
                              target="_top"
                            >
                              Register
                            </a>
                          )}
                        </div>

                        {(() => {
                          const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                          // Prefer WHY the event was recommended: the top-contributing
                          // dimension from the scorer (need x mix). An event picked for a
                          // Mind goal badges as Mind even when its dominant tag is Body.
                          const reasonKey: string | undefined = (event as any).reasons?.[0]?.dimension;
                          const reasonMeta = reasonKey ? wellnessDimensionMeta?.[reasonKey] : undefined;

                          let label: string | undefined;
                          let color: string | undefined;
                          let iconName: string | undefined;
                          if (reasonKey) {
                            label = reasonMeta?.label ?? capitalize(reasonKey);
                            color = reasonMeta?.color;
                            iconName = reasonMeta?.icon;
                          } else {
                            // No scoring reason (no need vector) — fall back to the
                            // event's dominant GoIcon dimension tag.
                            const dimId = (event as any).goiconMaxDimensionId;
                            const numId = Number(dimId);
                            label = Number.isFinite(numId)
                              ? (goiconDimensionIdToDrivenByLabel?.[numId] || (event as any).goiconMaxDimensionName)
                              : (event as any).goiconMaxDimensionName;
                            color = Number.isFinite(numId) ? goiconDimensionIdToColor?.[numId] : undefined;
                            iconName = Number.isFinite(numId) ? goiconDimensionIdToIcon?.[numId] : undefined;
                          }

                          const mix = (event as any).mix as Record<string, number> | undefined;
                          const mixParts = mix
                            ? Object.entries(mix)
                                .filter(([, v]) => (v ?? 0) >= 0.005)
                                .sort((a, b) => b[1] - a[1])
                                .map(([k, v]) => `${wellnessDimensionMeta?.[k]?.label ?? capitalize(k)} ${Math.round(v * 100)}%`)
                            : [];

                          return (
                            <>
                              {label && (
                                <span
                                  className="event-dimension-badge"
                                  style={color ? {
                                    backgroundColor: `${color}18`,
                                    color,
                                    borderColor: `${color}40`,
                                  } : undefined}
                                >
                                  {iconName && (
                                    <span className="event-dimension-badge-icon">
                                      <Icon name={iconName} size={14} decorative />
                                    </span>
                                  )}
                                  Supports: {label}
                                </span>
                              )}
                              {mixParts.length > 0 && (
                                <p className="event-mix-line">{mixParts.join(' · ')}</p>
                              )}
                            </>
                          );
                        })()}
                        {event.startTime && (
                          <p className="event-time">
                            {new Date(event.startTime).toLocaleString()}
                          </p>
                        )}
                        {event.location && (
                          <p className="event-location">Location: {event.location}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};
